/**
 * Visual Editor — unified two-column workspace that merges the Visual Style
 * File (defect/BOM/measure/photo modes) with the Design Studio (structured
 * markings, Alan proposals, buyer sign-off) into a single tabbed editor at
 * `/styles/$styleId/visual`.
 *
 * Five tabs share one canvas (GarmentFlat, per-type silhouettes) and one
 * right panel slot. The Design tab adds header extras (revision picker, tech
 * pack link, save button) and a below-canvas inspector.
 *
 * @see redesign-phases.md — Phase 1
 * @see prototype/garment/visual-style-file.html — canvas baseline
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useBlocker } from "@tanstack/react-router";
import {
  ArrowLeft,
  Box,
  Camera,
  Check,
  FileText,
  ImagePlus,
  Loader2,
  Move,
  Pencil,
  Ruler,
  ScanSearch,
} from "lucide-react";
import {
  Button,
  ConfirmDialog,
  PromptDialog,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusPill,
  toast,
} from "@flint/ui";
import { trpc } from "../../../../lib/trpc.js";
import { useS3Upload, type S3UploadStatus } from "../../../../lib/use-s3-upload.js";
import { styleStatusLabel, styleStatusTone } from "../../lib/status.js";
import { garmentTypeLabel } from "../../lib/garment-type.js";
import { GarmentFlat } from "../design-studio/garment-flat.tsx";
import { DraggablePin, clamp } from "./draggable-pin.tsx";
import { CanvasEmpty, Centered } from "./canvas-helpers.tsx";
import {
  DefectPanel,
  SEVERITY,
  UNZONED,
  ZONE_MAP,
  type DefectPin,
} from "./defect-tab.tsx";
import {
  BomPanel,
  BOM_KIND_ANCHOR,
  BOM_KIND_DOT,
  BOM_KIND_RING,
  type BomPin,
} from "./bom-tab.tsx";
import { MeasurePanel, cm, type Measurement } from "./measure-tab.tsx";
import { PhotoPanel } from "./photo-tab.tsx";
import {
  DesignInspector,
  DesignRail,
  PartColourPicker,
  ProposalGhost,
  StudioMarker,
  type Proposal,
  type StudioElement,
  type StudioElementType,
} from "./design-tab.tsx";

/* ── Tab type ─────────────────────────────────────────────────────────── */

export type EditorTab = "defect" | "bom" | "measure" | "photo" | "design";

type View = "front" | "back";

/* ── Page ─────────────────────────────────────────────────────────────── */

export function VisualEditorPage({
  styleId,
  initialTab,
}: {
  styleId: string;
  initialTab?: EditorTab | undefined;
}) {
  const utils = trpc.useUtils();

  /* ── Shared state ─────────────────────────────────────────────────── */
  const [activeTab, setActiveTabState] = useState<EditorTab>(
    initialTab ?? "defect",
  );
  const [view, setView] = useState<View>("front");
  const [selectedPinId, setSelectedPinId] = useState<string | null>(null);
  const [addingAt, setAddingAt] = useState<{ x: number; y: number } | null>(
    null,
  );
  const stageRef = useRef<HTMLDivElement>(null);

  /* ── Design-tab state ─────────────────────────────────────────────── */
  const [designVersion, setDesignVersion] = useState<number | undefined>(
    undefined,
  );
  const [elements, setElements] = useState<StudioElement[]>([]);
  const [dirty, setDirty] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [prompt, setPrompt] = useState("");
  const [saveOpen, setSaveOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [pendingTab, setPendingTab] = useState<EditorTab | null>(null);
  const pendingActionRef = useRef<(() => void) | null>(null);
  const savingViaGuardRef = useRef(false);

  /* ── Part selection + colour tinting (Phase 4b) ────────────────── */
  const [selectedPart, setSelectedPart] = useState<string | null>(null);
  const [partColourMap, setPartColourMap] = useState<Record<string, string>>(
    {},
  );

  /* ── Tab switching ────────────────────────────────────────────────── */
  const commitTabSwitch = (tab: EditorTab) => {
    setActiveTabState(tab);
    setSelectedPinId(null);
    setSelectedKey(null);
    setAddingAt(null);
    setSelectedPart(null);
    setPendingTab(null);
  };

  const setActiveTab = (tab: EditorTab) => {
    if (dirty && activeTab === "design" && tab !== "design") {
      setPendingTab(tab);
      return;
    }
    commitTabSwitch(tab);
  };

  /* ── Queries (all fire at mount) ──────────────────────────────────── */
  const styleQ = trpc.styles.get.useQuery({ id: styleId });
  const qcQ = trpc.qc.getForStyle.useQuery({ styleId });
  const materialsQ = trpc.materials.getForStyle.useQuery({ styleId });
  const measureQ = trpc.measurements.getForStyle.useQuery({ styleId });
  const designQ = trpc.garmentDesigns.getForStyle.useQuery({
    styleId,
    ...(designVersion !== undefined ? { version: designVersion } : {}),
  });

  const design = designQ.data ?? null;
  const latestVersion = design?.revisions[0]?.version ?? 1;
  const viewingOld =
    design !== null && design.currentVersion < latestVersion;
  const designReadOnly =
    design !== null && (design.archived || viewingOld);

  /* ── Hydrate design canvas when loaded revision changes ───────────── */
  useEffect(() => {
    if (!design) return;
    setElements(
      design.elements.map((el) => ({
        key: el.id,
        elementType: el.elementType as StudioElementType,
        view: el.view as View,
        x: el.x,
        y: el.y,
        widthMm: el.widthMm,
        heightMm: el.heightMm,
        offsetNote: el.offsetNote,
        note: el.note,
        artworkObjectKey: el.artworkObjectKey,
        artworkUrl: el.artworkUrl ?? null,
        colourway: el.colourway,
      })),
    );
    setDirty(false);
    setSelectedKey(null);
    setProposals([]);
  }, [design?.currentRevisionId]);

  /* ── Mutations: defect/bom/measure ────────────────────────────────── */
  const place = trpc.qc.place.useMutation({
    onSuccess: async () => {
      await utils.qc.getForStyle.invalidate({ styleId });
    },
    onError: (e: { message: string }) =>
      toast.error("Couldn't place pin", { description: e.message }),
  });
  const placeHotspot = trpc.materials.placeHotspot.useMutation({
    onSuccess: async () => {
      await utils.materials.getForStyle.invalidate({ styleId });
    },
    onError: (e: { message: string }) =>
      toast.error("Couldn't place hotspot", { description: e.message }),
  });
  const requestPhotoUpload = trpc.qc.requestPhotoUpload.useMutation();
  const attachPhoto = trpc.qc.attachPhoto.useMutation();
  const [uploadingId, setUploadingId] = useState<string | null>(null);

  const uploadDefectPhoto = async (defectId: string, file: File) => {
    if (
      !["image/jpeg", "image/png", "image/webp"].includes(file.type)
    ) {
      toast.error("Use a JPEG, PNG or WebP image");
      return;
    }
    setUploadingId(defectId);
    try {
      const { uploadUrl, objectKey } =
        await requestPhotoUpload.mutateAsync({
          contentType: file.type as
            | "image/jpeg"
            | "image/png"
            | "image/webp",
          size: file.size,
        });
      const put = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      if (!put.ok) throw new Error(`Upload failed (${put.status})`);
      await attachPhoto.mutateAsync({ defectId, objectKey });
      await utils.qc.getForStyle.invalidate({ styleId });
      toast.success("Photo attached");
    } catch (e) {
      toast.error("Couldn't attach photo", {
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setUploadingId(null);
    }
  };

  const addMeasure = trpc.measurements.add.useMutation({
    onSuccess: async () => {
      setAddingAt(null);
      setRemeasurePrefill(null);
      await utils.measurements.getForStyle.invalidate({ styleId });
      toast.success("Measurement saved");
    },
    onError: (e: { message: string }) =>
      toast.error("Couldn't save measurement", { description: e.message }),
  });

  /* ── Re-measure + quick-add state ───────────────────────────────────── */
  const [remeasurePrefill, setRemeasurePrefill] = useState<{
    pomCode: string;
    specMm: number;
    tolMm: number;
  } | null>(null);
  const [quickAddPending, setQuickAddPending] = useState(false);

  const onRemeasure = (m: Measurement) => {
    setRemeasurePrefill({
      pomCode: m.pomCode,
      specMm: m.specMm,
      tolMm: m.tolMm,
    });
    setAddingAt({ x: m.pinX, y: m.pinY });
    setSelectedPinId(null);
  };

  const onQuickAddSaveAll = async (
    rows: Array<{
      pomCode: string;
      specMm: number;
      tolMm: number;
      actualMm: number;
      pinX: number;
      pinY: number;
    }>,
  ) => {
    setQuickAddPending(true);
    try {
      // The rows are independent inserts — save them in parallel, and use
      // allSettled so one failure doesn't silently drop the rows after it.
      const results = await Promise.allSettled(
        rows.map((row) =>
          addMeasure.mutateAsync({
            styleId,
            pomCode: row.pomCode,
            specMm: row.specMm,
            tolMm: row.tolMm,
            actualMm: row.actualMm,
            pinX: row.pinX,
            pinY: row.pinY,
            pinView: view,
          }),
        ),
      );
      const saved = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - saved;
      if (failed === 0) {
        toast.success(`${saved} measurement${saved === 1 ? "" : "s"} saved`);
      } else {
        // Per-row failures are already toasted by the mutation's onError; this
        // summary makes the partial result explicit rather than silent.
        toast.error(`Saved ${saved} of ${results.length} — ${failed} failed`);
      }
    } finally {
      setQuickAddPending(false);
    }
  };

  /* ── Mutations: design tab ────────────────────────────────────────── */
  const invalidateDesign = async () => {
    await utils.garmentDesigns.getForStyle.invalidate({ styleId });
  };

  const save = trpc.garmentDesigns.saveRevision.useMutation({
    onSuccess: async (result) => {
      setSaveOpen(false);
      setDesignVersion(undefined);
      await invalidateDesign();
      toast.success(`Saved as v${result.version}`, {
        description:
          "Revisions are append-only — earlier versions stay intact.",
      });
      if (pendingActionRef.current) {
        const action = pendingActionRef.current;
        pendingActionRef.current = null;
        action();
      }
    },
    onError: (err) =>
      toast.error("Couldn't save the revision", {
        description: err.message,
      }),
  });

  const propose = trpc.garmentDesigns.propose.useMutation({
    onSuccess: (result) => {
      setProposals(
        result.proposals.map((p) => ({
          elementType: p.elementType as StudioElementType,
          view: p.view as View,
          x: p.x,
          y: p.y,
          widthMm: p.widthMm ?? null,
          heightMm: p.heightMm ?? null,
          offsetNote: p.offsetNote ?? null,
          note: p.note ?? null,
          artworkObjectKey: null,
          artworkUrl: null,
          colourway: null,
          rationale: p.rationale,
        })),
      );
      setPrompt("");
    },
    onError: (err) =>
      toast.error("Alan couldn't propose that", {
        description: err.message,
      }),
  });

  const approve = trpc.garmentDesigns.approve.useMutation({
    onSuccess: async () => {
      setApproveOpen(false);
      await invalidateDesign();
      toast.success("Buyer sign-off noted", {
        description: `v${design?.currentVersion} is pinned as the signed-off revision.`,
      });
    },
    onError: (err) =>
      toast.error("Couldn't record the sign-off", {
        description: err.message,
      }),
  });

  /* ── Artwork upload (Phase 4c, two-phase S3 via useS3Upload) ──────── */
  const requestArtwork = trpc.garmentDesigns.requestArtworkUpload.useMutation();
  const confirmArtwork = trpc.garmentDesigns.confirmArtworkUpload.useMutation();
  const confirmResultRef = useRef<{ objectKey: string; artworkUrl: string | null } | null>(null);
  // Pin the artwork to the element that STARTED the upload — the user may
  // select a different element while the S3 round-trip is in flight, and
  // `selectedKey` would otherwise be read fresh in onSuccess (wrong element).
  const artworkTargetKeyRef = useRef<string | null>(null);

  const artworkUpload = useS3Upload({
    request: {
      mutateAsync: async (input) => {
        if (!design) throw new Error("No design loaded");
        artworkTargetKeyRef.current = selectedKey;
        return requestArtwork.mutateAsync({
          designId: design.id,
          contentType: input.contentType as "image/jpeg" | "image/png" | "image/webp",
          size: input.size,
        });
      },
    },
    confirm: {
      mutateAsync: async (input) => {
        const result = await confirmArtwork.mutateAsync({ objectKey: input.objectKey });
        confirmResultRef.current = result;
        return result;
      },
    },
    allowedTypes: ["image/jpeg", "image/png", "image/webp"],
    maxBytes: 20 * 1024 * 1024,
    invalidateQueries: [],
    onSuccess: () => {
      const result = confirmResultRef.current;
      const targetKey = artworkTargetKeyRef.current;
      if (result && targetKey) {
        updateElement(targetKey, {
          artworkObjectKey: result.objectKey,
          artworkUrl: result.artworkUrl,
        });
      }
      confirmResultRef.current = null;
      artworkTargetKeyRef.current = null;
    },
  });

  /* ── Unsaved-changes guards (Phase 5) ────────────────────────────── */
  useEffect(() => {
    if (!dirty) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const navBlocker = useBlocker({
    shouldBlockFn: () => dirty,
    withResolver: true,
  });

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        if (dirty && activeTab === "design" && !designReadOnly && !save.isPending) {
          setSaveOpen(true);
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [dirty, activeTab, designReadOnly, save.isPending]);

  /* ── Design element helpers ───────────────────────────────────────── */
  const addElement = (type: StudioElementType) => {
    const key = `draft-${type}-${elements.length}-${Math.trunc(performance.now())}`;
    setElements((cur) => [
      ...cur,
      {
        key,
        elementType: type,
        view,
        x: 50,
        y: 40,
        widthMm: null,
        heightMm: null,
        offsetNote: null,
        note: null,
        artworkObjectKey: null,
        artworkUrl: null,
        colourway: null,
      },
    ]);
    setSelectedKey(key);
    setDirty(true);
  };

  const updateElement = (key: string, patch: Partial<StudioElement>) => {
    setElements((cur) =>
      cur.map((e) => (e.key === key ? { ...e, ...patch } : e)),
    );
    setDirty(true);
  };

  const removeElement = (key: string) => {
    setElements((cur) => cur.filter((e) => e.key !== key));
    if (selectedKey === key) setSelectedKey(null);
    setDirty(true);
  };

  const acceptProposal = (index: number) => {
    const p = proposals[index];
    if (!p) return;
    const key = `alan-${index}-${Math.trunc(performance.now())}`;
    setElements((cur) => [...cur, { ...p, key, artworkUrl: null }]);
    setProposals((cur) => cur.filter((_, i) => i !== index));
    setDirty(true);
    if (p.view !== view) setView(p.view);
  };

  const submitSave = (note: string) => {
    if (!design) return;
    save.mutate({
      designId: design.id,
      note: note.trim() === "" ? null : note.trim(),
      elements: elements.map((e) => ({
        elementType: e.elementType,
        view: e.view,
        x: e.x,
        y: e.y,
        widthMm: e.widthMm,
        heightMm: e.heightMm,
        offsetNote: e.offsetNote,
        note: e.note,
        artworkObjectKey: e.artworkObjectKey,
        colourway: e.colourway,
      })),
    });
  };

  /* ── Derived data ─────────────────────────────────────────────────── */
  const defects = useMemo<DefectPin[]>(() => {
    const flat = (qcQ.data?.lots ?? []).flatMap((lot) =>
      lot.defects.map((d) => ({ d, lotRef: lot.lotRef })),
    );
    const byZone = new Map<string, number>();
    const list: DefectPin[] = [];
    let n = 0;
    for (const { d, lotRef } of flat) {
      const anchor = ZONE_MAP[d.zone ?? ""] ?? UNZONED;
      const i = byZone.get(d.zone ?? "__none__") ?? 0;
      byZone.set(d.zone ?? "__none__", i + 1);
      const angle = i * 1.1;
      const r = i > 0 ? 7 : 0;
      list.push({
        ...d,
        label: `D${++n}`,
        lotRef,
        anchorX: anchor.x + Math.cos(angle) * r,
        anchorY: anchor.y + Math.sin(angle) * r,
      });
    }
    return list;
  }, [qcQ.data]);

  const bomLines = materialsQ.data?.hasBom ? materialsQ.data.lines : [];
  const bomPins: BomPin[] = bomLines.map((l, i) => {
    const anchor = BOM_KIND_ANCHOR[l.kind] ?? UNZONED;
    return {
      ...l,
      label: `B${i + 1}`,
      anchorX: anchor.x,
      anchorY: anchor.y,
    };
  });
  const bomUnplaced = bomPins.filter((b) => b.pinView === null).length;
  const photoDefects = defects.filter((p) => p.photoKey);
  const measurements: Measurement[] = measureQ.data?.measurements ?? [];
  const measureOnView = measurements.filter((m) => m.pinView === view);
  const unplacedDefects = defects.filter((p) => p.pinView === null).length;

  // Pin-level selections
  const selectedDefect =
    activeTab === "defect"
      ? defects.find((p) => p.id === selectedPinId) ?? null
      : null;
  const selectedBom =
    activeTab === "bom"
      ? bomPins.find((b) => b.bomLineId === selectedPinId) ?? null
      : null;
  const selectedMeasure =
    activeTab === "measure"
      ? measurements.find((m) => m.id === selectedPinId) ?? null
      : null;

  // Design elements on current view
  const onViewElements = useMemo(
    () => elements.filter((e) => e.view === view),
    [elements, view],
  );
  const onViewProposals = useMemo(
    () => proposals.filter((p) => p.view === view),
    [proposals, view],
  );
  const selectedElement =
    elements.find((e) => e.key === selectedKey) ?? null;

  // A placed pin shows on its own face; an un-placed one shows (at its anchor) on the front.
  const onView = (pinView: string | null) =>
    pinView ? pinView === view : view === "front";
  const renderDefectPins =
    activeTab === "defect"
      ? defects.filter((p) => onView(p.pinView))
      : activeTab === "photo"
        ? photoDefects.filter((p) => onView(p.pinView))
        : [];
  const renderBomPins =
    activeTab === "bom"
      ? bomPins.filter((b) => onView(b.pinView))
      : [];

  /* ── Canvas click for measure tab ─────────────────────────────────── */
  const onStageAdd = (e: { clientX: number; clientY: number }) => {
    const rect = stageRef.current?.getBoundingClientRect();
    if (!rect) return;
    setSelectedPinId(null);
    setAddingAt({
      x: clamp(((e.clientX - rect.left) / rect.width) * 100),
      y: clamp(((e.clientY - rect.top) / rect.height) * 100),
    });
  };

  /* ── Colour palette from ratio grid (Phase 4b) ──────────────────── */
  const colourPalette = useMemo(
    () =>
      (styleQ.data?.grid?.byColour ?? []).map((c) => ({
        colour: c.colour,
        colourHex: c.colourHex,
      })),
    [styleQ.data?.grid?.byColour],
  );

  /* ── Loading gate (only blocks on style — tabs show their own state) */
  if (styleQ.isLoading) return <Centered>Loading…</Centered>;
  const style = styleQ.data;
  if (!style) return <Centered>Style not found.</Centered>;

  /* ── Render ───────────────────────────────────────────────────────── */
  return (
    <div className="flex min-h-full flex-col bg-bg">
      {/* ── Header ──────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-20 flex h-14 items-center gap-3.5 border-b border-line bg-paper px-6">
        <Link
          to="/merchandising/styles/$styleId/overview"
          params={{ styleId }}
          aria-label="Back to Style File"
          className="grid h-9 w-9 shrink-0 place-items-center rounded-sm border border-line text-ink-2 transition hover:bg-bg"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div className="min-w-0 flex-1">
          <h1 className="flex items-center gap-2 text-base font-bold leading-tight tracking-tight text-ink">
            <span className="font-mono">{style.styleRef}</span>
            <StatusPill
              tone={styleStatusTone(style.status)}
              size="sm"
              dot
            >
              {styleStatusLabel(style.status)}
            </StatusPill>
            <span className="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-semibold uppercase tracking-wider text-sky-700">
              Visual
            </span>
          </h1>
          <p className="mt-0.5 truncate text-xs text-muted">
            {style.name} · {garmentTypeLabel(style.garmentType)} ·{" "}
            {style.buyerName}
          </p>
        </div>

        {/* Design-tab header extras */}
        {activeTab === "design" && design ? (
          <>
            <Select
              value={String(design.currentVersion)}
              onValueChange={(v) =>
                setDesignVersion(
                  Number(v) === latestVersion ? undefined : Number(v),
                )
              }
            >
              <SelectTrigger className="h-8 w-40 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {design.revisions.map((r) => (
                  <SelectItem key={r.id} value={String(r.version)}>
                    v{r.version}
                    {r.isApproved ? " · signed off" : ""}
                    {r.version === latestVersion ? " · latest" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              intent="ghost"
              size="sm"
              className="border border-line bg-paper"
              asChild
            >
              <Link
                to="/merchandising/styles/$styleId/design/pack"
                params={{ styleId }}
              >
                <FileText size={14} />
                Tech pack
              </Link>
            </Button>
            <Button
              size="sm"
              disabled={designReadOnly || !dirty || save.isPending}
              onClick={() => setSaveOpen(true)}
            >
              {save.isPending ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Check size={14} />
              )}
              Save v{latestVersion + 1}
            </Button>
          </>
        ) : (
          <Link
            to="/merchandising/styles/$styleId/overview"
            params={{ styleId }}
            className="rounded-sm border border-line bg-paper px-3 py-1.5 text-xs font-medium text-ink-2 transition hover:bg-bg"
          >
            ← Style File
          </Link>
        )}
      </header>

      {/* Old-version banner (design tab only) */}
      {activeTab === "design" && viewingOld ? (
        <div className="border-b border-line bg-warn-50 px-6 py-2 text-xs text-ink-2">
          Viewing v{design?.currentVersion} of {latestVersion} — read-only.{" "}
          <button
            type="button"
            className="font-semibold text-sky-700 hover:underline"
            onClick={() => setDesignVersion(undefined)}
          >
            Jump to latest
          </button>
        </div>
      ) : null}

      <div className="w-full px-6 pb-16 pt-5">
        {/* ── Two-column grid ──────────────────────────────────────── */}
        <div className="grid gap-4 lg:grid-cols-[7fr_3fr]">
          {/* ── Left: Canvas ─────────────────────────────────────── */}
          <section className="flex flex-col gap-3">
            <div className="rounded-lg border border-line bg-paper">
              {/* View toggle + contextual hint */}
              <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-2.5">
                <div className="inline-flex overflow-hidden rounded-sm border border-line">
                  {(["front", "back"] as const).map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => {
                        setView(v);
                        setSelectedPinId(null);
                        setSelectedKey(null);
                        setSelectedPart(null);
                      }}
                      className={`px-3 py-1 text-xs font-semibold uppercase tracking-wider transition ${
                        view === v
                          ? "bg-sky-50 text-sky-700"
                          : "bg-paper text-muted hover:bg-bg"
                      }`}
                    >
                      {v}
                    </button>
                  ))}
                </div>
                <CanvasHint
                  tab={activeTab}
                  counts={{ defects: defects.length, bom: bomPins.length, markings: onViewElements.length }}
                  selectedPart={activeTab === "design" ? selectedPart : null}
                />
              </div>

              {/* Stage */}
              <div
                ref={stageRef}
                className="relative mx-auto my-2 w-full max-w-[440px] touch-none select-none"
                style={{ aspectRatio: "400 / 500" }}
              >
                {/* Blueprint grid */}
                <div
                  className="absolute inset-0 rounded-md bg-[length:24px_24px] bg-[image:linear-gradient(to_right,rgb(0_0_0/0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgb(0_0_0/0.03)_1px,transparent_1px)]"
                  aria-hidden
                />

                {/* GarmentFlat replaces GarmentOutline */}
                <div className="absolute inset-0">
                  <GarmentFlat
                    garmentType={style.garmentType}
                    view={view}
                    onPartClick={
                      activeTab === "design" && !designReadOnly
                        ? (part) => setSelectedPart(part || null)
                        : undefined
                    }
                    selectedPart={
                      activeTab === "design"
                        ? (selectedPart ?? undefined)
                        : undefined
                    }
                    partColours={
                      Object.keys(partColourMap).length > 0
                        ? partColourMap
                        : undefined
                    }
                  />
                </div>

                {/* ── Tab-specific canvas overlays ───────────────── */}

                {/* Defect / Photo pins */}
                {(activeTab === "defect" || activeTab === "photo") &&
                  renderDefectPins.map((p) => {
                    const sev = SEVERITY[p.severity];
                    const placed =
                      p.pinX !== null && p.pinY !== null;
                    return (
                      <DraggablePin
                        key={p.id}
                        x={placed ? p.pinX! : p.anchorX}
                        y={placed ? p.pinY! : p.anchorY}
                        label={p.label}
                        dotClass={sev.dot}
                        ringClass={sev.ring}
                        selected={p.id === selectedPinId}
                        placed={placed}
                        stageRef={stageRef}
                        title={`${p.label} · ${p.defectType} · ${sev.label}${placed ? "" : " · drag to place"}`}
                        onPlace={(x, y) =>
                          place.mutate({
                            defectId: p.id,
                            pinX: x,
                            pinY: y,
                            pinView: view,
                          })
                        }
                        onSelect={() =>
                          setSelectedPinId(
                            p.id === selectedPinId ? null : p.id,
                          )
                        }
                      />
                    );
                  })}

                {/* BOM hotspots */}
                {activeTab === "bom" &&
                  renderBomPins.map((b) => {
                    const placed =
                      b.pinX !== null && b.pinY !== null;
                    return (
                      <DraggablePin
                        key={b.bomLineId}
                        x={placed ? b.pinX! : b.anchorX}
                        y={placed ? b.pinY! : b.anchorY}
                        label={b.label}
                        dotClass={
                          BOM_KIND_DOT[b.kind] ?? "bg-sky-500"
                        }
                        ringClass={
                          BOM_KIND_RING[b.kind] ??
                          "ring-sky-500/30"
                        }
                        selected={b.bomLineId === selectedPinId}
                        placed={placed}
                        stageRef={stageRef}
                        title={`${b.label} · ${b.kind}${placed ? "" : " · drag to place"}`}
                        onPlace={(x, y) =>
                          placeHotspot.mutate({
                            bomLineId: b.bomLineId,
                            pinX: x,
                            pinY: y,
                            pinView: view,
                          })
                        }
                        onSelect={() =>
                          setSelectedPinId(
                            b.bomLineId === selectedPinId
                              ? null
                              : b.bomLineId,
                          )
                        }
                      />
                    );
                  })}

                {/* Measure: click-to-add overlay + POM pins + ghost */}
                {activeTab === "measure" ? (
                  <div
                    className="absolute inset-0 z-[5] cursor-crosshair"
                    onClick={onStageAdd}
                    aria-label="Click the garment to add a point of measure"
                  />
                ) : null}
                {activeTab === "measure" &&
                  measureOnView.map((m, i) => {
                    const on = m.id === selectedPinId;
                    return (
                      <button
                        key={m.id}
                        type="button"
                        style={{
                          left: `${m.pinX}%`,
                          top: `${m.pinY}%`,
                        }}
                        title={`${m.pomCode} · ${cm(m.actualMm)}/${cm(m.specMm)} ${m.uom}`}
                        onClick={() => {
                          setSelectedPinId(on ? null : m.id);
                          setAddingAt(null);
                        }}
                        className={`absolute z-10 -translate-x-1/2 -translate-y-1/2 grid h-6 w-6 place-items-center rounded-full text-xs font-bold text-paper shadow-sm ring-4 transition ${m.inTolerance ? "bg-emerald-500" : "bg-bad"} ${on ? "scale-125 ring-sky-200" : m.inTolerance ? "ring-emerald-500/30" : "ring-bad/30"}`}
                      >
                        M{i + 1}
                      </button>
                    );
                  })}
                {activeTab === "measure" && addingAt ? (
                  <span
                    style={{
                      left: `${addingAt.x}%`,
                      top: `${addingAt.y}%`,
                    }}
                    className="absolute z-20 -translate-x-1/2 -translate-y-1/2 grid h-6 w-6 animate-pulse place-items-center rounded-full bg-sky-500 text-xs font-bold text-paper shadow ring-4 ring-sky-200"
                    aria-hidden
                  >
                    +
                  </span>
                ) : null}

                {/* Design: studio markers + proposal ghosts */}
                {activeTab === "design" &&
                  onViewElements.map((el) => (
                    <StudioMarker
                      key={el.key}
                      element={el}
                      selected={el.key === selectedKey}
                      readOnly={designReadOnly}
                      stageRef={stageRef}
                      onSelect={() => setSelectedKey(el.key)}
                      onPlace={(x, y) =>
                        updateElement(el.key, { x, y })
                      }
                    />
                  ))}
                {activeTab === "design" &&
                  onViewProposals.map((p, i) => (
                    <ProposalGhost
                      key={`proposal-${i}`}
                      proposal={p}
                      index={i}
                    />
                  ))}

                {/* ── Empty states ────────────────────────────────── */}
                {activeTab === "defect" && defects.length === 0 ? (
                  <CanvasEmpty>
                    No defects pinned yet — record an end-line inspection in
                    QC §7.
                  </CanvasEmpty>
                ) : null}
                {activeTab === "defect" &&
                view === "back" &&
                renderDefectPins.length === 0 &&
                defects.length > 0 ? (
                  <CanvasEmpty>
                    No defects on the back — drag a pin here while on this
                    view to place one.
                  </CanvasEmpty>
                ) : null}
                {activeTab === "measure" &&
                !addingAt &&
                measureOnView.length === 0 ? (
                  <CanvasEmpty>
                    Click the garment to drop a point-of-measure pin.
                  </CanvasEmpty>
                ) : null}
                {activeTab === "bom" && bomPins.length === 0 ? (
                  <CanvasEmpty>
                    The BOM is generated when costing is locked (§4).
                  </CanvasEmpty>
                ) : null}
                {activeTab === "bom" &&
                view === "back" &&
                renderBomPins.length === 0 &&
                bomPins.length > 0 ? (
                  <CanvasEmpty>
                    No hotspots on the back — drag one here to place it.
                  </CanvasEmpty>
                ) : null}
                {activeTab === "photo" && photoDefects.length === 0 ? (
                  <CanvasEmpty>
                    No photos attached yet — select a defect in the panel to
                    upload one.
                  </CanvasEmpty>
                ) : null}
                {activeTab === "design" &&
                onViewElements.length === 0 &&
                elements.length === 0 ? (
                  <CanvasEmpty>
                    No markings yet — add one from the panel, or describe
                    the design to Alan.
                  </CanvasEmpty>
                ) : null}
              </div>
            </div>

            {/* Part colour picker (below canvas, when a garment part is selected) */}
            {activeTab === "design" && selectedPart && !designReadOnly ? (
              <PartColourPicker
                selectedPart={selectedPart}
                palette={colourPalette}
                currentHex={partColourMap[selectedPart] ?? null}
                onPick={(hex) =>
                  setPartColourMap((prev) => {
                    if (hex === null) {
                      const next = { ...prev };
                      delete next[selectedPart];
                      return next;
                    }
                    return { ...prev, [selectedPart]: hex };
                  })
                }
              />
            ) : null}

            {/* Design inspector (below canvas, left column) */}
            {activeTab === "design" ? (
              <DesignInspector
                selected={selectedElement}
                readOnly={designReadOnly}
                palette={colourPalette}
                onUpdate={updateElement}
                onRemove={removeElement}
                artworkUpload={
                  selectedElement && !designReadOnly ? (
                    <div className="flex items-center gap-2">
                      <Button
                        intent="ghost"
                        size="sm"
                        className="border border-line bg-paper"
                        disabled={artworkUpload.isBusy}
                        onClick={artworkUpload.openPicker}
                      >
                        {artworkUpload.isBusy ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <ImagePlus size={14} />
                        )}
                        Upload artwork
                      </Button>
                      <input
                        ref={artworkUpload.inputRef}
                        className="hidden"
                        {...artworkUpload.inputProps}
                      />
                      <ArtworkStatusText status={artworkUpload.status} />
                    </div>
                  ) : null
                }
              />
            ) : null}
          </section>

          {/* ── Right: Tab bar + panel ─────────────────────────────── */}
          <aside className="flex w-full flex-col gap-4">
            {/* ── Tab bar ──────────────────────────────────────────── */}
            <div className="flex items-center gap-0.5 rounded-md border border-line bg-paper p-1">
              <TabButton
                icon={ScanSearch}
                label="Defect"
                count={defects.length}
                active={activeTab === "defect"}
                onClick={() => setActiveTab("defect")}
              />
              <TabButton
                icon={Box}
                label="BOM"
                count={bomLines.length}
                active={activeTab === "bom"}
                onClick={() => setActiveTab("bom")}
              />
              <TabButton
                icon={Ruler}
                label="Measure"
                count={measurements.length}
                active={activeTab === "measure"}
                onClick={() => setActiveTab("measure")}
              />
              <TabButton
                icon={Camera}
                label="Photo"
                count={photoDefects.length}
                active={activeTab === "photo"}
                onClick={() => setActiveTab("photo")}
              />
              <TabButton
                icon={Pencil}
                label="Design"
                count={elements.length}
                active={activeTab === "design"}
                badge={activeTab !== "design" && dirty}
                onClick={() => setActiveTab("design")}
              />
            </div>

            {activeTab !== "design" ? (
              <div className="rounded-lg border border-line bg-paper p-4">
                {activeTab === "defect" ? (
                  <DefectPanel
                    pin={selectedDefect}
                    pins={defects}
                    dhuTenths={qcQ.data?.dhuTenths ?? 0}
                    rftPercent={qcQ.data?.rftPercent ?? 100}
                    blocked={
                      qcQ.data?.hasUnresolvedRejection ?? false
                    }
                    unplaced={unplacedDefects}
                    onPick={setSelectedPinId}
                    onBack={() => setSelectedPinId(null)}
                    uploading={uploadingId === selectedDefect?.id}
                    onUploadPhoto={(file) => {
                      if (selectedDefect)
                        void uploadDefectPhoto(selectedDefect.id, file);
                    }}
                  />
                ) : null}
                {activeTab === "bom" ? (
                  <BomPanel
                    pins={bomPins}
                    selected={selectedBom}
                    unplaced={bomUnplaced}
                    onPick={(id) => setSelectedPinId(id)}
                    onBack={() => setSelectedPinId(null)}
                  />
                ) : null}
                {activeTab === "measure" ? (
                  <MeasurePanel
                    measurements={measurements}
                    selected={selectedMeasure}
                    addingAt={addingAt}
                    view={view}
                    pending={addMeasure.isPending}
                    prefill={remeasurePrefill}
                    quickAddPending={quickAddPending}
                    onPick={(id) => {
                      setSelectedPinId(id);
                      setAddingAt(null);
                    }}
                    onBack={() => setSelectedPinId(null)}
                    onCancelAdd={() => {
                      setAddingAt(null);
                      setRemeasurePrefill(null);
                    }}
                    onSave={(f) => {
                      if (!addingAt) return;
                      addMeasure.mutate({
                        styleId,
                        pomCode: f.pomCode,
                        specMm: f.specMm,
                        tolMm: f.tolMm,
                        actualMm: f.actualMm,
                        pinX: addingAt.x,
                        pinY: addingAt.y,
                        pinView: view,
                      });
                    }}
                    onRemeasure={onRemeasure}
                    onQuickAddSaveAll={onQuickAddSaveAll}
                  />
                ) : null}
                {activeTab === "photo" ? (
                  <PhotoPanel
                    pins={photoDefects}
                    allDefects={defects}
                    selectedPinId={selectedPinId}
                    onSelectPin={setSelectedPinId}
                    uploading={uploadingId !== null}
                    onUploadPhoto={(defectId, file) =>
                      void uploadDefectPhoto(defectId, file)
                    }
                  />
                ) : null}
              </div>
            ) : null}

            {/* Design rail — spans multiple cards below the panel wrapper */}
            {activeTab === "design" && design ? (
              <DesignRail
                readOnly={designReadOnly}
                elements={elements}
                selectedKey={selectedKey}
                dirty={dirty}
                currentVersion={design.currentVersion}
                archived={design.archived}
                prompt={prompt}
                proposals={proposals}
                proposePending={propose.isPending}
                approvePending={approve.isPending}
                onAddElement={addElement}
                onSelectElement={setSelectedKey}
                onSetView={setView}
                onPromptChange={setPrompt}
                onPropose={() =>
                  propose.mutate({
                    designId: design.id,
                    prompt: prompt.trim(),
                    view,
                  })
                }
                onAcceptProposal={acceptProposal}
                onDiscardProposal={(i) =>
                  setProposals((c) =>
                    c.filter((_, idx) => idx !== i),
                  )
                }
                onApproveOpen={() => setApproveOpen(true)}
              />
            ) : activeTab === "design" ? (
              // design is null: surface loading / error rather than a blank tab.
              <Centered>
                {designQ.isLoading
                  ? "Loading design…"
                  : designQ.error
                    ? "Couldn't load the design. Please retry."
                    : "No design for this style yet."}
              </Centered>
            ) : null}
          </aside>
        </div>
      </div>

      {/* ── Dialogs (design tab) ────────────────────────────────────── */}
      {design ? (
        <>
          <PromptDialog
            open={saveOpen}
            onOpenChange={(open) => {
              setSaveOpen(open);
              if (!open) pendingActionRef.current = null;
            }}
            title={`Save as v${latestVersion + 1}`}
            description="A one-line note for the revision history — what changed?"
            label="Change note"
            placeholder="e.g. added chest logo + kaja detail"
            confirmLabel="Save revision"
            required={false}
            onConfirm={submitSave}
          />
          <PromptDialog
            open={approveOpen}
            onOpenChange={setApproveOpen}
            title={`Note buyer sign-off — v${design.currentVersion}`}
            description="A non-blocking annotation that the buyer signed off this revision (e.g. over WhatsApp). The revision stays pinned even after later edits."
            label="Sign-off note"
            placeholder="e.g. approved by Beacon on WhatsApp"
            confirmLabel="Note sign-off"
            required={false}
            onConfirm={(note) =>
              approve.mutate({
                designId: design.id,
                revisionId: design.currentRevisionId,
                note: note.trim() === "" ? null : note.trim(),
              })
            }
          />
        </>
      ) : null}

      {/* ── Unsaved-changes guard dialogs (Phase 5) ───────────────────── */}
      {pendingTab !== null ? (
        <ConfirmDialog
          open={!saveOpen}
          onOpenChange={(open) => {
            if (!open && !savingViaGuardRef.current) {
              setPendingTab(null);
              pendingActionRef.current = null;
            }
            savingViaGuardRef.current = false;
          }}
          title="Unsaved design changes"
          description="You have unsaved changes to the design markings. Save them as a new revision, or discard?"
          confirmLabel="Save & switch"
          cancelLabel="Keep editing"
          onConfirm={() => {
            const tab = pendingTab;
            savingViaGuardRef.current = true;
            pendingActionRef.current = () => commitTabSwitch(tab);
            setSaveOpen(true);
          }}
          footnote={
            <button
              type="button"
              className="underline hover:text-ink-2"
              onClick={() => {
                if (design) {
                  setElements(
                    design.elements.map((el) => ({
                      key: el.id,
                      elementType: el.elementType as StudioElementType,
                      view: el.view as View,
                      x: el.x,
                      y: el.y,
                      widthMm: el.widthMm,
                      heightMm: el.heightMm,
                      offsetNote: el.offsetNote,
                      note: el.note,
                      artworkObjectKey: el.artworkObjectKey,
                      artworkUrl: el.artworkUrl ?? null,
                      colourway: el.colourway,
                    })),
                  );
                  setProposals([]);
                }
                setDirty(false);
                commitTabSwitch(pendingTab);
              }}
            >
              Discard & switch
            </button>
          }
        />
      ) : null}
      {navBlocker.status === "blocked" ? (
        <ConfirmDialog
          open={!saveOpen}
          onOpenChange={(open) => {
            if (!open && !savingViaGuardRef.current) {
              navBlocker.reset();
              pendingActionRef.current = null;
            }
            savingViaGuardRef.current = false;
          }}
          title="Unsaved design changes"
          description="You have unsaved changes to the design markings. Save them as a new revision, or discard?"
          confirmLabel="Save & leave"
          cancelLabel="Keep editing"
          onConfirm={() => {
            savingViaGuardRef.current = true;
            pendingActionRef.current = () => navBlocker.proceed();
            setSaveOpen(true);
          }}
          footnote={
            <button
              type="button"
              className="underline hover:text-ink-2"
              onClick={() => navBlocker.proceed()}
            >
              Discard & leave
            </button>
          }
        />
      ) : null}
    </div>
  );
}

/* ── Private sub-components ───────────────────────────────────────────── */

function TabButton({
  icon: Icon,
  label,
  count,
  active,
  badge,
  onClick,
}: {
  icon: typeof Box;
  label: string;
  count?: number;
  active: boolean;
  badge?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative flex flex-1 items-center justify-center gap-1 rounded-sm px-1.5 py-1.5 text-xs font-semibold transition ${
        active
          ? "bg-sky-600 text-white shadow-sm"
          : "text-ink-2 hover:bg-bg"
      }`}
    >
      <Icon size={12} /> {label}
      {count !== undefined ? (
        <span
          className={`rounded-full px-1 text-xs font-bold leading-4 ${
            active
              ? "bg-sky-700 text-white"
              : "bg-bg text-muted"
          }`}
        >
          {count}
        </span>
      ) : null}
      {badge ? (
        <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-warn" />
      ) : null}
    </button>
  );
}

function CanvasHint({
  tab,
  counts,
  selectedPart,
}: {
  tab: EditorTab;
  counts: { defects: number; bom: number; markings: number };
  selectedPart?: string | null;
}) {
  if (tab === "defect" && counts.defects > 0) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted">
        <Move size={12} /> Drag a pin to place · click to inspect
      </span>
    );
  }
  if (tab === "bom" && counts.bom > 0) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted">
        <Move size={12} /> Drag a hotspot to where the material goes
      </span>
    );
  }
  if (tab === "measure") {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted">
        <Ruler size={12} /> Click the garment to add a POM
      </span>
    );
  }
  if (tab === "design") {
    if (selectedPart) {
      const partLabel = selectedPart.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
      return (
        <span className="text-xs text-sky-700">
          Selected: {partLabel} — pick a colourway to tint
        </span>
      );
    }
    return (
      <span className="text-xs text-muted">
        {counts.markings} marking{counts.markings === 1 ? "" : "s"} on this
        view · click a garment part to colour
      </span>
    );
  }
  return null;
}

function ArtworkStatusText({ status }: { status: S3UploadStatus }) {
  if (status.kind === "uploading") {
    const label = status.phase === "request" ? "Requesting…" : status.phase === "put" ? "Uploading…" : "Confirming…";
    return <span className="text-xs text-muted">{label}</span>;
  }
  if (status.kind === "saved") {
    return <span className="text-xs text-good">Artwork saved</span>;
  }
  if (status.kind === "error") {
    return <span className="text-xs text-bad">{status.message}</span>;
  }
  return null;
}
